import React from "react";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { STATUS_COLORS, STATUS_LABELS, SERVICE_TYPES } from "../constants";
import { formatHour12, getTechnicianName } from "../utils";
import { formatServiceDate } from "../../../utils/dateUtils";
import { C } from "../theme";

const GRID_COLS =
  "minmax(90px,.6fr) minmax(110px,.8fr) minmax(0,1.4fr) minmax(0,1fr) 80px 70px 72px";

function SortHeader({ label, field, orderBy, orderDirection, onSort }) {
  const active = orderBy === field;
  return (
    <Typography
      onClick={() => onSort?.(field)}
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: C.textSoft,
        textTransform: "uppercase",
        cursor: "pointer",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        gap: 0.3,
        "&:hover": { color: C.primary },
      }}
    >
      {label}
      {active &&
        (orderDirection === "asc" ? (
          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        ))}
    </Typography>
  );
}

function ServiceRow({
  service,
  technicians,
  selected,
  onSelect,
  onEdit,
  onDelete,
}) {
  const isSelected =
    selected?._id === service._id || selected?.id === service.id;
  const statusColor = STATUS_COLORS[service.status] || C.primary;
  const typeLabel =
    SERVICE_TYPES.find((t) => t.value === service.type)?.label || service.type;

  const techLabel = service.technicians?.length
    ? service.technicians.map((t) => t?.name || t).join(", ")
    : service.technician
      ? getTechnicianName(service, technicians)
      : "Sin asignar";

  return (
    <Box
      onClick={() => onSelect(service)}
      sx={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        gap: 1.5,
        alignItems: "center",
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${C.border}`,
        cursor: "pointer",
        bgcolor: isSelected ? C.primarySoft : "#fff",
        borderLeft: isSelected ? `3px solid ${C.primary}` : "3px solid transparent",
        transition: "background-color .12s",
        "&:hover": { bgcolor: isSelected ? C.primarySoft : "#f9fafb" },
      }}
    >
      <Chip
        label={STATUS_LABELS[service.status] || service.status}
        size="small"
        sx={{
          height: 24,
          fontWeight: 700,
          fontSize: 11,
          bgcolor: `${statusColor}18`,
          color: statusColor,
          border: `1px solid ${statusColor}40`,
          maxWidth: "100%",
        }}
      />

      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
        {typeLabel}
      </Typography>

      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {service.client?.name || "—"}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: C.textSoft, mt: 0.2 }}>
          {service.branch?.name || "—"}
        </Typography>
      </Box>

      <Typography noWrap sx={{ fontSize: 13, color: C.textSoft }}>
        {techLabel}
      </Typography>

      <Typography noWrap sx={{ fontSize: 13, color: C.textSoft }}>
        {service.date
          ? formatServiceDate(service.date, "es-ES", {
              day: "2-digit",
              month: "short",
            })
          : "—"}
      </Typography>

      <Typography noWrap sx={{ fontSize: 13, color: C.textSoft }}>
        {formatHour12(service.hour)}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(service);
          }}
        >
          <EditIcon sx={{ fontSize: 17, color: C.primary }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(service);
          }}
        >
          <DeleteIcon sx={{ fontSize: 17, color: C.danger }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function ServiceList({
  services,
  technicians,
  onSelect,
  onEdit,
  onDelete,
  selected,
  orderBy,
  orderDirection,
  onSort,
}) {
  if (services.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
          color: C.textSoft,
        }}
      >
        <Typography sx={{ fontSize: 14 }}>No hay servicios para mostrar</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: GRID_COLS,
          gap: 1.5,
          px: 2,
          py: 1.25,
          bgcolor: "#fafafa",
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
        }}
      >
        <SortHeader label="Estado" field="status" orderBy={orderBy} orderDirection={orderDirection} onSort={onSort} />
        <SortHeader label="Tipo" field="type" orderBy={orderBy} orderDirection={orderDirection} onSort={onSort} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textSoft, textTransform: "uppercase" }}>
          Cliente / Sucursal
        </Typography>
        <SortHeader label="Técnico" field="technician" orderBy={orderBy} orderDirection={orderDirection} onSort={onSort} />
        <SortHeader label="Fecha" field="date" orderBy={orderBy} orderDirection={orderDirection} onSort={onSort} />
        <SortHeader label="Hora" field="hour" orderBy={orderBy} orderDirection={orderDirection} onSort={onSort} />
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: C.textSoft,
            textTransform: "uppercase",
            textAlign: "right",
          }}
        >
          Acciones
        </Typography>
      </Box>

      {services.map((service) => (
        <ServiceRow
          key={service._id || service.id}
          service={service}
          technicians={technicians}
          selected={selected}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </Box>
  );
}
